'use client'

import { useState, useEffect, useCallback } from 'react'
import Link from 'next/link'
import { useTheme } from '@/contexts/ThemeContext'
import { getSupabaseClient } from '@/utils/supabase/singleton'
import type { DivingTour } from '@/types/tour'

type FilterType = 'all' | 'domestic' | 'overseas'

const FILTERS: { value: FilterType; label: string }[] = [
  { value: 'all', label: '전체' },
  { value: 'domestic', label: '국내' },
  { value: 'overseas', label: '해외' },
]

function SeatGauge({ current, max, urgent }: { current: number; max: number; urgent?: boolean }) {
  const pct = max > 0 ? Math.min((current / max) * 100, 100) : 0
  return (
    <div className="od-gauge-track w-full">
      <div className={`od-gauge-fill ${urgent ? 'urgent' : ''}`} style={{ width: `${pct}%` }} />
    </div>
  )
}

export default function ToursPage() {
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const [tours, setTours] = useState<DivingTour[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<FilterType>('all')

  const loadTours = useCallback(async () => {
    try {
      setIsLoading(true)
      const supabase = getSupabaseClient()
      let query = supabase
        .from('diving_tours')
        .select(`*, instructor_profiles (display_name, is_verified)`)
        .in('status', ['recruiting', 'almost_full'])
        .order('is_urgent', { ascending: false })
        .order('start_date', { ascending: true })
      if (filterType !== 'all') query = query.eq('tour_type', filterType)
      const { data, error } = await query
      if (error) throw error
      setTours(data || [])
    } catch (e) {
      console.error('투어 로드 실패:', e)
    } finally {
      setIsLoading(false)
    }
  }, [filterType])

  useEffect(() => { loadTours() }, [loadTours])

  useEffect(() => {
    const handleVisibility = () => { if (!document.hidden) loadTours() }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [loadTours])

  const formatPrice = (price: number) => new Intl.NumberFormat('ko-KR').format(price)

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    return `${d.getMonth() + 1}/${d.getDate()}`
  }

  const filteredTours = tours.filter(tour => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      tour.title.toLowerCase().includes(q) ||
      tour.destination.toLowerCase().includes(q) ||
      (tour.country && tour.country.toLowerCase().includes(q))
    )
  })

  const bg = isDark ? '#0f172a' : '#f4f7fb'
  const ink = isDark ? '#e8f0fb' : '#0a1f3a'
  const secondary = isDark ? '#94a3b8' : '#6b7684'
  const card = isDark ? '#1e2a3d' : '#ffffff'
  const border = isDark ? '#2c3a52' : '#e9eff7'

  return (
    <div style={{ background: bg, minHeight: '100vh' }}>

      {/* 헤더 */}
      <div className="px-5 pt-8 pb-4 max-w-2xl mx-auto">
        <h1 style={{ font: '700 22px/30px sans-serif', color: ink, margin: '0 0 4px' }}>투어</h1>
        <p style={{ font: '400 13px/20px sans-serif', color: secondary, margin: 0 }}>
          강사와 함께하는 국내외 프리다이빙 투어
        </p>
      </div>

      {/* 검색 */}
      <div className="px-5 mb-4 max-w-2xl mx-auto">
        <div
          className="flex items-center gap-2 px-3 py-2.5 rounded-xl"
          style={{ background: card, border: `1px solid ${border}` }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={secondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
          </svg>
          <input
            type="text"
            placeholder="목적지, 투어명 검색"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{
              background: 'transparent', border: 'none', outline: 'none',
              font: '400 14px/22px sans-serif', color: ink, flex: 1,
            }}
          />
        </div>
      </div>

      {/* 칩 필터 */}
      <div className="chip-rail px-5 mb-6 max-w-2xl mx-auto">
        {FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setFilterType(f.value)}
            className="flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all"
            style={{
              background: filterType === f.value
                ? (isDark ? '#1e3a5f' : '#0a1f3a')
                : (isDark ? '#1e2a3d' : '#ffffff'),
              color: filterType === f.value ? '#ffffff' : secondary,
              border: `1px solid ${filterType === f.value ? 'transparent' : border}`,
              font: '500 13px/20px sans-serif',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* 투어 목록 */}
      <div className="px-5 pb-16 max-w-2xl mx-auto">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="rounded-2xl p-4 animate-pulse" style={{ background: card, height: 100 }} />
            ))}
          </div>
        ) : filteredTours.length === 0 ? (
          <div
            className="rounded-2xl p-10 text-center"
            style={{ background: card, border: `1px solid ${border}` }}
          >
            <p style={{ font: '400 14px/22px sans-serif', color: secondary }}>
              {searchQuery ? '검색 결과가 없습니다' : '모집 중인 투어가 없습니다'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTours.map(tour => {
              const filled = tour.current_participants || 0
              const total = tour.max_participants || 0
              const remaining = total - filled
              const isUrgent = tour.is_urgent || (total > 0 && remaining <= 2)
              const pctFull = total > 0 ? Math.round((filled / total) * 100) : 0

              return (
                <Link
                  key={tour.id}
                  href={`/tours/${tour.id}`}
                  className="block rounded-2xl p-4 transition-all"
                  style={{ background: card, border: `1px solid ${border}` }}
                >
                  {/* tag row */}
                  <div className="flex items-center gap-2 mb-2">
                    <span
                      className="px-2 py-0.5 rounded text-xs font-medium"
                      style={{
                        background: isDark ? '#1e3a5f' : '#e9eff7',
                        color: isDark ? '#5ea6ff' : '#143560',
                        font: '500 11px/16px sans-serif',
                      }}
                    >
                      {tour.tour_type === 'overseas' ? '🌍 해외' : '🇰🇷 국내'}
                    </span>
                    {tour.required_level && (
                      <span style={{ font: '400 11px/16px sans-serif', color: secondary }}>
                        {tour.required_level}
                      </span>
                    )}
                    {isUrgent && (
                      <span className="flex items-center gap-1">
                        <span
                          className="od-breath inline-block rounded-full"
                          style={{ width: 5, height: 5, background: '#0748ff' }}
                        />
                        <span style={{ font: '500 11px/16px sans-serif', color: '#0748ff' }}>임박</span>
                      </span>
                    )}
                  </div>

                  {/* title + price */}
                  <div className="flex items-start justify-between gap-3 mb-1.5">
                    <p
                      className="flex-1 line-clamp-2"
                      style={{ font: '600 14px/22px sans-serif', color: ink, margin: 0 }}
                    >
                      {tour.title}
                    </p>
                    <span
                      className="od-breath flex-shrink-0"
                      style={{
                        font: '700 15px/22px sans-serif',
                        color: isUrgent ? '#0748ff' : (isDark ? '#5ea6ff' : '#2b6df0'),
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatPrice(tour.price)}원
                    </span>
                  </div>

                  {/* meta row */}
                  <p style={{ font: '400 12px/18px sans-serif', color: secondary, margin: '0 0 8px' }}>
                    {tour.destination}
                    {tour.start_date ? ` · ${formatDate(tour.start_date)}${tour.end_date ? ` ~ ${formatDate(tour.end_date)}` : ''}` : ''}
                    {tour.instructor_profiles ? ` · ${tour.instructor_profiles.display_name}` : ''}
                  </p>

                  {/* seat gauge */}
                  {total > 0 && (
                    <div>
                      <SeatGauge current={filled} max={total} urgent={isUrgent} />
                      <p style={{ font: '400 11px/16px sans-serif', color: isUrgent ? '#0748ff' : secondary, margin: '3px 0 0', fontVariantNumeric: 'tabular-nums' }}>
                        {remaining}자리 남음 ({pctFull}% 참여)
                      </p>
                    </div>
                  )}
                </Link>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
